/*
 * Delivery location picker.
 *
 * Collects a map pin, municipality, and Qatar blue plate (zone / street / building) and saves
 * them as cart attributes — Shopify copies those onto the order as note_attributes,
 * where the Rafeq delivery integration reads latitude/longitude/zone/street/building.
 *
 * Both directions are checked against Qatar's national address register (QARS), a
 * public ArcGIS service that allows this storefront's origin:
 *  - typed blue plate  -> pin jumps to that exact registered building
 *  - pin / GPS         -> blue plate filled from the nearest registered building
 *
 * Map: Google Maps when a browser key is configured and accepted; otherwise (no key,
 * key rejected, or script blocked) the free OpenStreetMap/Leaflet map.
 */
(function () {
  'use strict';

  const dialog = document.querySelector('[data-dloc-dialog]');
  const configEl = document.getElementById('DeliveryLocationConfig');
  if (!dialog || !configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (_) {
    return;
  }
  const S = config.strings || {};
  const QARS_URL = 'https://services.gisqatar.org.qa/server/rest/services/Vector/QARS_wgs84/MapServer/0/query';
  const MUNICIPALITY_URL = 'https://services.gisqatar.org.qa/server/rest/services/Vector/MunicipalityE/MapServer/0/query';
  const DOHA = { lat: 25.2854, lng: 51.531 };
  const NEARBY_RADIUS_M = 150;
  const NEARBY_MAX = 4;

  const form = dialog.querySelector('[data-dloc-form]');
  const mapEl = dialog.querySelector('[data-dloc-map]');
  const saveBtn = dialog.querySelector('[data-dloc-save]');
  const plateStatus = dialog.querySelector('[data-dloc-plate-status]');
  const errorEl = dialog.querySelector('[data-dloc-error]');
  const nearbyEl = dialog.querySelector('[data-dloc-nearby]');
  const noPlateBox = dialog.querySelector('[data-dloc-noplate]');
  const checkoutFallback = dialog.querySelector('[data-dloc-checkout-fallback]');
  const inputs = {};
  dialog.querySelectorAll('[data-dloc-input]').forEach((el) => {
    inputs[el.getAttribute('data-dloc-input')] = el;
  });

  const saved = config.saved || {};
  const state = {
    lat: toNum(saved.latitude),
    lng: toNum(saved.longitude),
    savedOnCart: toNum(saved.latitude) != null && toNum(saved.longitude) != null && Boolean(saved.city) &&
      (Boolean(saved.zone && saved.street && saved.building) || saved.noBluePlate === 'true'),
    platePoint: null,
    // True once the blue plate is confirmed: verified by typing, chosen from nearby buildings,
    // or auto-matched to a building right at the pin.
    plateOk: Boolean(saved.zone && saved.street && saved.building)
  };
  let pendingCheckout = null;
  let checkoutBusy = false;
  let checkoutBypass = false;
  let pinSeq = 0;
  let citySeq = 0;
  let cityManuallyEdited = false;

  /* ---------- helpers ---------- */

  function toNum(v) {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }

  function digits(v) {
    return String(v || '')
      .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
      .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
      .trim();
  }

  function fill(template, p) {
    return String(template || '')
      .replace('[z]', p.zone)
      .replace('[s]', p.street)
      .replace('[b]', p.building);
  }

  function distanceM(a, b) {
    const r = 6371000;
    const rad = (d) => (d * Math.PI) / 180;
    const x = Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
      Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
    return 2 * r * Math.asin(Math.sqrt(x));
  }

  function setStatus(text, tone) {
    plateStatus.textContent = text || '';
    plateStatus.dataset.tone = tone || '';
  }

  function showError(text) {
    errorEl.textContent = text || '';
    errorEl.hidden = !text;
  }

  function refreshSaveButton() {
    const hasPin = state.lat != null && state.lng != null;
    saveBtn.disabled = !(hasPin && inputs.city.value.trim() && (state.plateOk || noPlateBox.checked));
  }

  function setPlate(p, ok) {
    inputs.zone.value = p ? p.zone : '';
    inputs.street.value = p ? p.street : '';
    inputs.building.value = p ? p.building : '';
    state.plateOk = Boolean(p && ok);
    state.platePoint = p ? { lat: p.lat, lng: p.lng } : null;
    refreshSaveButton();
  }

  function hideNearby() {
    nearbyEl.hidden = true;
    nearbyEl.innerHTML = '';
  }

  function showNearby(list) {
    nearbyEl.innerHTML = '';
    const hint = document.createElement('p');
    hint.className = 'dloc__nearby-hint';
    hint.textContent = S.nearestSelectedHint;
    nearbyEl.appendChild(hint);
    list.slice(0, NEARBY_MAX).forEach((b) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'dloc__chip';
      chip.setAttribute('aria-pressed', b === list[0] ? 'true' : 'false');
      chip.textContent = b.zone + ' / ' + b.street + ' / ' + b.building;
      chip.title = S.zoneShort + ' ' + b.zone + ' · ' + S.streetShort + ' ' + b.street + ' · ' + S.buildingShort + ' ' + b.building;
      chip.addEventListener('click', () => {
        nearbyEl.querySelectorAll('.dloc__chip').forEach((c) => c.setAttribute('aria-pressed', 'false'));
        chip.setAttribute('aria-pressed', 'true');
        noPlateBox.checked = false;
        setPlate(b, true);
        setPin(b.lat, b.lng, 18);
        void lookupCity(b.lat, b.lng);
        setStatus(fill(S.pinFilled, b), 'ok');
        showError('');
      });
      nearbyEl.appendChild(chip);
    });
    nearbyEl.hidden = false;
  }

  // Point-in-polygon lookup against Qatar's municipality boundaries.
  async function lookupCity(lat, lng) {
    const seq = ++citySeq;
    cityManuallyEdited = false;
    inputs.city.value = '';
    refreshSaveButton();
    const qs = new URLSearchParams({
      where: '1=1', geometry: lng + ',' + lat,
      geometryType: 'esriGeometryPoint', inSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      outFields: 'ENAME,ANAME', returnGeometry: 'false', f: 'json'
    });
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(MUNICIPALITY_URL + '?' + qs.toString(), { signal: controller.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      if (json.error) throw new Error('service');
      if (seq !== citySeq || cityManuallyEdited) return;
      const name = json.features && json.features[0] && json.features[0].attributes;
      inputs.city.value = (name && name.ENAME || '').trim();
    } catch (_) {
      // Leave the field editable when the GIS service is unavailable.
    } finally {
      clearTimeout(timer);
      if (seq === citySeq) refreshSaveButton();
    }
  }

  /* ---------- national address register ---------- */

  async function qars(params) {
    const qs = new URLSearchParams(Object.assign({
      outFields: 'ZONE_NO,STREET_NO,BUILDING_NO',
      returnGeometry: 'true',
      outSR: '4326',
      f: 'json'
    }, params));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(QARS_URL + '?' + qs.toString(), { signal: controller.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      if (json.error) throw new Error('service');
      return (json.features || [])
        .filter((f) => f.geometry && Number.isFinite(f.geometry.x) && Number.isFinite(f.geometry.y))
        .map((f) => ({
          zone: String(f.attributes.ZONE_NO),
          street: String(f.attributes.STREET_NO),
          building: String(f.attributes.BUILDING_NO),
          lat: f.geometry.y,
          lng: f.geometry.x
        }));
    } finally {
      clearTimeout(timer);
    }
  }

  function plateNumber(v) {
    const m = digits(v).match(/^(\d{1,5})/);
    return m ? Number.parseInt(m[1], 10) : null;
  }

  async function lookupPlate(zone, street, building) {
    const z = plateNumber(zone);
    const s = plateNumber(street);
    const b = plateNumber(building);
    if (!z || !s || !b) return null;
    const hits = await qars({ where: 'ZONE_NO=' + z + ' AND STREET_NO=' + s + ' AND BUILDING_NO=' + b, resultRecordCount: '5' });
    return hits[0] || null;
  }

  async function nearbyBuildings(lat, lng) {
    const hits = await qars({
      geometry: lng + ',' + lat,
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      distance: String(NEARBY_RADIUS_M),
      units: 'esriSRUnit_Meter'
    });
    return hits
      .map((h) => Object.assign({ d: distanceM({ lat, lng }, h) }, h))
      .filter((h) => h.d <= NEARBY_RADIUS_M)
      .sort((a, b) => a.d - b.d);
  }

  /* ---------- map (Google with OpenStreetMap fallback) ---------- */

  let map = null; // { setPin(lat, lng, zoom), resize() }
  let mapObserver = null;
  let mapPromise = null;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function loadCss(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    document.head.appendChild(l);
  }

  function googleMap() {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('google timeout')), 10000);
      // Google calls this when the key is rejected (missing API, wrong referrer, billing).
      window.gm_authFailure = () => {
        clearTimeout(timer);
        reject(new Error('google auth'));
      };
      window.__dlocGoogleReady = () => {
        try {
          const g = window.google.maps;
          const center = state.lat != null ? { lat: state.lat, lng: state.lng } : DOHA;
          const gmap = new g.Map(mapEl, {
            center,
            zoom: state.lat != null ? 17 : 11,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            gestureHandling: 'greedy'
          });
          const marker = new g.Marker({ map: gmap, draggable: true, visible: state.lat != null, position: center });
          gmap.addListener('click', (e) => onUserPin(e.latLng.lat(), e.latLng.lng()));
          marker.addListener('dragend', (e) => onUserPin(e.latLng.lat(), e.latLng.lng()));
          // Auth failures surface after the map is created; give Google a moment before trusting it.
          setTimeout(() => {
            clearTimeout(timer);
            resolve({
              setPin(lat, lng, zoom) {
                marker.setPosition({ lat, lng });
                marker.setVisible(true);
                gmap.panTo({ lat, lng });
                if (zoom) gmap.setZoom(zoom);
              },
              resize() {}
            });
          }, 1500);
        } catch (err) {
          clearTimeout(timer);
          reject(err);
        }
      };
      loadScript('https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(config.googleKey) +
        '&loading=async&callback=__dlocGoogleReady&language=' + (config.lang === 'ar' ? 'ar' : 'en') + '&region=QA')
        .catch((err) => { clearTimeout(timer); reject(err); });
    });
  }

  async function leafletMap() {
    loadCss('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    if (!window.L) await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
    const L = window.L;
    mapEl.innerHTML = '';
    const center = state.lat != null ? [state.lat, state.lng] : [DOHA.lat, DOHA.lng];
    const lmap = L.map(mapEl, { zoomControl: true }).setView(center, state.lat != null ? 17 : 11);
    lmap.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
    }).addTo(lmap);
    const marker = L.marker(center, { draggable: true });
    if (state.lat != null) marker.addTo(lmap);
    lmap.on('click', (e) => onUserPin(e.latlng.lat, e.latlng.lng));
    marker.on('dragend', () => {
      const p = marker.getLatLng();
      onUserPin(p.lat, p.lng);
    });
    return {
      setPin(lat, lng, zoom) {
        marker.setLatLng([lat, lng]);
        if (!lmap.hasLayer(marker)) marker.addTo(lmap);
        lmap.setView([lat, lng], zoom || lmap.getZoom());
      },
      resize() {
        lmap.invalidateSize();
      }
    };
  }

  function ensureMap() {
    if (!mapPromise) {
      mapPromise = (config.googleKey ? googleMap().catch(() => leafletMap()) : leafletMap()).then((m) => {
        map = m;
        return m;
      });
    }
    return mapPromise;
  }

  /* ---------- interactions ---------- */

  function setPin(lat, lng, zoom) {
    state.lat = lat;
    state.lng = lng;
    if (map) map.setPin(lat, lng, zoom);
    refreshSaveButton();
    showError('');
  }

  // Pin set by the customer (tap, drag or GPS): fill the blue plate from the register.
  async function onUserPin(lat, lng) {
    const seq = ++pinSeq;
    ++plateSeq;
    clearTimeout(plateTimer);
    setPin(lat, lng);
    hideNearby();
    setPlate(null, false);
    void lookupCity(lat, lng);
    setStatus(S.checking, 'busy');
    try {
      const list = await nearbyBuildings(lat, lng);
      if (seq !== pinSeq) return;
      const nearest = list[0];
      if (nearest && !noPlateBox.checked) {
        setPlate(nearest, true);
        setStatus(fill(S.pinFilled, nearest), 'ok');
        showNearby(list);
      } else if (list.length) {
        setStatus(S.noPlateNote, 'warn');
      } else {
        setStatus(S.pinNoBuilding, 'warn');
      }
    } catch (_) {
      if (seq === pinSeq) setStatus(S.lookupDown, 'warn');
    }
    refreshSaveButton();
  }

  let plateTimer = null;
  let plateSeq = 0;
  function onPlateInput() {
    clearTimeout(plateTimer);
    ++pinSeq;
    const seq = ++plateSeq;
    ++citySeq;
    inputs.city.value = '';
    ['zone', 'street', 'building'].forEach((k) => {
      const v = digits(inputs[k].value);
      if (v !== inputs[k].value) inputs[k].value = v;
    });
    const z = inputs.zone.value;
    const s = inputs.street.value;
    const b = inputs.building.value;
    state.plateOk = false;
    refreshSaveButton();
    if (!z || !s || !b) {
      setStatus('', '');
      return;
    }
    plateTimer = setTimeout(async () => {
      setStatus(S.checking, 'busy');
      try {
        const hit = await lookupPlate(z, s, b);
        if (seq !== plateSeq) return;
        if (hit) {
          state.platePoint = { lat: hit.lat, lng: hit.lng };
          state.plateOk = true;
          noPlateBox.checked = false;
          hideNearby();
          await ensureMap();
          if (seq !== plateSeq) return;
          setPin(hit.lat, hit.lng, 18);
          void lookupCity(hit.lat, hit.lng);
          setStatus(S.plateFound, 'ok');
        } else {
          state.platePoint = null;
          state.plateOk = false;
          setStatus(S.plateNotFound, 'error');
        }
        refreshSaveButton();
      } catch (_) {
        if (seq === plateSeq) setStatus(S.lookupDown, 'warn');
      }
    }, 450);
  }

  function useGps() {
    if (!navigator.geolocation) {
      showError(S.gpsDenied);
      return;
    }
    const btn = dialog.querySelector('[data-dloc-gps]');
    btn.classList.add('is-loading');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        btn.classList.remove('is-loading');
        await ensureMap();
        map.setPin(pos.coords.latitude, pos.coords.longitude, 18);
        onUserPin(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        btn.classList.remove('is-loading');
        showError(S.gpsDenied);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  }

  function updateRows(attrs) {
    const hasPlate = attrs.zone && attrs.street && attrs.building;
    document.querySelectorAll('[data-dloc-row]').forEach((row) => {
      row.classList.add('is-set');
      const title = row.querySelector('[data-dloc-row-title]');
      const detail = row.querySelector('[data-dloc-row-detail]');
      const btn = row.querySelector('[data-dloc-open]');
      if (title) title.textContent = S.rowTitleSet;
      if (detail) detail.textContent = attrs.delivery_city + ' · ' + (hasPlate ? fill(S.rowPlate, attrs) : S.rowPinned);
      if (btn) btn.textContent = S.rowChange;
    });
  }

  async function save(event) {
    event.preventDefault();
    if (state.lat == null || state.lng == null) {
      showError(S.needPin);
      return;
    }
    if (!state.plateOk && !noPlateBox.checked) {
      showError(S.needPlate);
      return;
    }
    if (!inputs.city.value.trim()) {
      showError(S.needCity);
      return;
    }
    const lat = state.lat.toFixed(6);
    const lng = state.lng.toFixed(6);
    const attrs = {
      latitude: lat,
      longitude: lng,
      delivery_city: inputs.city.value.trim(),
      delivery_no_blue_plate: noPlateBox.checked ? 'true' : 'false',
      zone: digits(inputs.zone.value),
      street: digits(inputs.street.value),
      building: digits(inputs.building.value),
      floor: inputs.floor.value.trim(),
      apartment: inputs.apartment.value.trim(),
      'Delivery map': 'https://www.google.com/maps?q=' + lat + ',' + lng
    };
    saveBtn.classList.add('is-loading');
    saveBtn.disabled = true;
    try {
      const routes = (window.HamamTheme && window.HamamTheme.routes) || {};
      const res = await fetch(routes.cartUpdate || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const cart = await res.json();
      state.savedOnCart = true;
      updateRows(attrs);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'attributes' } }));
      // Continuing to checkout navigates away, so close at once; otherwise slide out.
      if (pendingCheckout) dialog.close();
      else closeDialog();
      if (pendingCheckout) {
        const btn = pendingCheckout;
        pendingCheckout = null;
        btn.click();
      }
    } catch (_) {
      showError(S.saveFailed);
    } finally {
      saveBtn.classList.remove('is-loading');
      refreshSaveButton();
    }
  }

  const desktop = window.matchMedia('(min-width: 900px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Desktop: the picker is a drawer, so it slides back out instead of vanishing.
  function closeDialog() {
    if (!dialog.open || dialog.classList.contains('is-closing')) return;
    if (!desktop.matches || reducedMotion.matches) {
      dialog.close();
      return;
    }
    dialog.classList.add('is-closing');
    setTimeout(() => {
      dialog.classList.remove('is-closing');
      dialog.close();
    }, 260);
  }

  function open() {
    checkoutFallback.hidden = true;
    showError(pendingCheckout ? S.requiredNotice : '');
    // With the cart drawer open on a wide screen, dock beside it so the picker slides out of the cart.
    const cartDrawer = document.querySelector('#CartDrawer[data-open]');
    const besideCart = Boolean(cartDrawer) && window.innerWidth >= 960;
    if (besideCart) {
      // Dock exactly at the cart's edge, whatever width the cart drawer has (and in RTL).
      const r = cartDrawer.getBoundingClientRect();
      const width = document.documentElement.dir === 'rtl' ? r.right : document.documentElement.clientWidth - r.left;
      dialog.style.setProperty('--dloc-cart-w', Math.round(width) + 'px');
    }
    dialog.classList.toggle('dloc-dialog--beside-cart', besideCart);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    ensureMap().then((m) => {
      // The map fills whatever height is left in the picker, so redraw it
      // whenever its box changes (status lines, building chips, rotation).
      if (!mapObserver && 'ResizeObserver' in window) {
        mapObserver = new ResizeObserver(() => m.resize());
        mapObserver.observe(dialog.querySelector('[data-dloc-map]'));
      }
      m.resize();
      if (state.lat != null) m.setPin(state.lat, state.lng, 17);
    });
    refreshSaveButton();
  }

  /* ---------- wiring ---------- */

  ['zone', 'street', 'building'].forEach((k) => inputs[k].addEventListener('input', onPlateInput));
  inputs.city.addEventListener('input', () => {
    cityManuallyEdited = true;
    refreshSaveButton();
  });
  dialog.querySelector('[data-dloc-gps]').addEventListener('click', useGps);
  dialog.querySelector('[data-dloc-close]').addEventListener('click', () => {
    pendingCheckout = null;
    closeDialog();
  });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      pendingCheckout = null;
      closeDialog();
    }
  });
  dialog.addEventListener('cancel', (e) => {
    // Esc: animate out like the other close paths.
    e.preventDefault();
    pendingCheckout = null;
    closeDialog();
  });
  form.addEventListener('submit', save);
  noPlateBox.addEventListener('change', () => {
    if (noPlateBox.checked) {
      setPlate(null, false);
      hideNearby();
      setStatus(S.noPlateNote, 'warn');
    } else {
      setStatus('', '');
    }
    refreshSaveButton();
  });

  // The Ajax cart keeps the delivery details for fulfillment, but its checkout
  // does not copy them into Shopify's shipping address fields. Build a Storefront
  // cart with the same lines and a selected delivery address at checkout time.
  function storefrontLine(item) {
    if (item.has_components || item.parent_relationship) throw new Error('bundle');
    if (!Number.isInteger(item.variant_id) || !Number.isInteger(item.quantity) || item.quantity < 1) {
      throw new Error('line');
    }
    const line = { merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id, quantity: item.quantity };
    const properties = item.properties || {};
    const attributes = Object.entries(properties).map(([key, value]) => {
      if (value == null || typeof value === 'object') throw new Error('property');
      return { key, value: String(value) };
    });
    if (attributes.length) line.attributes = attributes;
    const planId = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id;
    if (planId) line.sellingPlanId = 'gid://shopify/SellingPlan/' + planId;
    return line;
  }

  function checkoutAddress(attributes) {
    const city = String(attributes.delivery_city || '').trim();
    const zone = String(attributes.zone || '').trim();
    const street = String(attributes.street || '').trim();
    const building = String(attributes.building || '').trim();
    const hasPlate = zone && street && building;
    if (!city || (!hasPlate && attributes.delivery_no_blue_plate !== 'true')) throw new Error('address');
    const address1 = hasPlate
      ? 'Building ' + building + ', Street ' + street + ', Zone ' + zone
      : 'Map pin ' + attributes.latitude + ', ' + attributes.longitude;
    const address2 = [attributes.floor && 'Floor ' + attributes.floor, attributes.apartment]
      .filter(Boolean).join(', ');
    return { address1, ...(address2 ? { address2 } : {}), city, countryCode: 'QA' };
  }

  function checkoutDiscountCodes(cart) {
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || [])
        .map((allocation) => allocation.discount_application))
    ];
    return [...new Set(applications.filter((app) => app && app.type === 'discount_code')
      .map((app) => app.title).filter(Boolean))];
  }

  async function addressedCheckoutUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartResponse = await fetch(root + 'cart.js', { credentials: 'same-origin' });
    if (!cartResponse.ok) throw new Error('cart');
    const cart = await cartResponse.json();
    if (!cart.items || !cart.items.length || cart.items.length > 250) throw new Error('cart');
    const address = checkoutAddress(cart.attributes || {});
    const discounts = checkoutDiscountCodes(cart);
    const input = {
      buyerIdentity: { countryCode: 'QA' },
      lines: cart.items.map(storefrontLine),
      attributes: Object.entries(cart.attributes || {}).filter(([key, value]) => key && value != null)
        .map(([key, value]) => ({ key, value: String(value) })),
      delivery: { addresses: [{ address: { deliveryAddress: address }, selected: true, oneTimeUse: true }] }
    };
    if (cart.note) input.note = cart.note;
    if (discounts.length) input.discountCodes = discounts;
    const query = 'mutation CreateAddressedCart($input: CartInput) @inContext(language: ' + (document.documentElement.lang.toLowerCase().startsWith('ar') ? 'AR' : 'EN') + ') {' +
      ' cartCreate(input: $input) {' +
      ' cart { checkoutUrl totalQuantity cost { totalAmount { amount currencyCode } }' +
      ' discountCodes { code applicable } delivery { addresses { selected address {' +
      ' ... on CartDeliveryAddress { address1 city countryCode } } } } }' +
      ' userErrors { field message } warnings { message } } }';
    const response = await fetch('/api/2026-07/graphql.json', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables: { input } })
    });
    if (!response.ok) throw new Error('storefront');
    const result = await response.json();
    const payload = result.data && result.data.cartCreate;
    if (result.errors?.length || !payload?.cart || payload.userErrors?.length) throw new Error('storefront');
    const created = payload.cart;
    const selected = created.delivery?.addresses?.find((entry) => entry.selected)?.address;
    const total = created.cost?.totalAmount;
    if (created.totalQuantity !== cart.item_count || selected?.address1 !== address.address1 ||
      selected?.city !== address.city || selected?.countryCode !== 'QA' ||
      total?.currencyCode !== cart.currency || Math.round(Number(total.amount) * 100) !== cart.total_price ||
      discounts.some((code) => !created.discountCodes?.some((item) => item.code === code && item.applicable))) {
      throw new Error('cart-mismatch');
    }
    if (!created.checkoutUrl) throw new Error('checkout-url');
    return created.checkoutUrl;
  }

  checkoutFallback.addEventListener('click', () => {
    const button = pendingCheckout;
    pendingCheckout = null;
    if (!button) return;
    checkoutBypass = true;
    dialog.close();
    button.click();
  });

  // Rows are re-rendered inside the cart drawer, so listen at the document.
  document.addEventListener('click', (e) => {
    // The whole cart row opens the picker, not just its button (bigger tap target).
    const trigger = e.target.closest && (e.target.closest('[data-dloc-open]') || e.target.closest('[data-dloc-row]'));
    if (trigger) {
      e.preventDefault();
      open();
    }
  });

  // Cart page and drawer checkout buttons share the same prefilled handoff.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[name="checkout"]');
    if (!btn) return;
    if (checkoutBypass) { checkoutBypass = false; return; }
    if (!state.savedOnCart && config.required) {
      e.preventDefault();
      e.stopImmediatePropagation();
      pendingCheckout = btn;
      open();
      return;
    }
    if (!state.savedOnCart) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      const url = await addressedCheckoutUrl();
      window.location.assign(url);
    } catch (_) {
      pendingCheckout = btn;
      open();
      showError(S.checkoutHandoffFailed);
      checkoutFallback.hidden = false;
    } finally {
      checkoutBusy = false;
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }, true);

  refreshSaveButton();
})();

